import * as React from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface PlayerAvatarProps {
  src?: string;
  name: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  isOnline?: boolean;
  className?: string;
  showOnlineStatus?: boolean;
}

const sizeClasses = {
  xs: "h-6 w-6",
  sm: "h-8 w-8",
  md: "h-10 w-10", 
  lg: "h-12 w-12",
  xl: "h-16 w-16",
};

const onlineIndicatorSizes = {
  xs: "h-1.5 w-1.5 -bottom-0.5 -right-0.5",
  sm: "h-2 w-2 -bottom-0.5 -right-0.5",
  md: "h-3 w-3 -bottom-1 -right-1",
  lg: "h-3 w-3 -bottom-1 -right-1", 
  xl: "h-4 w-4 -bottom-1 -right-1",
};

export function PlayerAvatar({ 
  src, 
  name, 
  size = "md", 
  isOnline, 
  className,
  showOnlineStatus = true 
}: PlayerAvatarProps) {
  // Normalize empty strings to undefined to prevent recursive page loads
  const normalizedSrc = src && src.trim() !== "" ? src : undefined;
  const [imgSrc, setImgSrc] = React.useState(normalizedSrc);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    const newSrc = src && src.trim() !== "" ? src : undefined;
    setImgSrc(newSrc);
    setHasError(false);
  }, [src]);

  return (
    <div className={cn("relative", className)}>
      <Avatar className={sizeClasses[size]}>
        <AvatarImage 
          src={imgSrc} 
          alt={`${name}'s profile`}
          onError={() => {
            if (!hasError) {
              setHasError(true);
              setImgSrc("/avatar-placeholder.png");
            }
          }}
        />
        <AvatarFallback className={cn(
          "font-medium",
          size === "sm" ? "text-xs" : size === "lg" || size === "xl" ? "text-base" : "text-sm"
        )}>
          {name.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      
      {showOnlineStatus && isOnline && (
        <div className={cn(
          "absolute bg-green-500 border-2 border-background rounded-full",
          onlineIndicatorSizes[size]
        )}>
          <div className="w-full h-full bg-green-400 rounded-full animate-pulse" />
        </div>
      )}
    </div>
  );
}
